import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateAccountClick?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onCreateAccountClick }) => {
  const [mode, setMode] = useState<'login' | 'forgot' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const { signIn, resetPassword, resendConfirmation, needsPasswordReset, clearPasswordReset } = useAuth();
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);

  // Check for password reset token in URL / auth state
  useEffect(() => {
    const hash = window.location.hash;
    if ((hash && hash.includes('type=recovery')) || needsPasswordReset) {
      setMode('reset');
    }
  }, [needsPasswordReset]);

  if (!isOpen && mode !== 'reset') return null;
  
  // Always show modal if in reset mode (from URL)
  if (mode === 'reset' && !isOpen) {
    // Force show the modal for password reset
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          const needsConfirmation =
            error.message.toLowerCase().includes('email not confirmed') ||
            error.message.toLowerCase().includes('not confirmed');
          setShowResendConfirmation(needsConfirmation);
          setError(
            needsConfirmation
              ? 'Your email address has not been confirmed yet. Check your inbox for the confirmation link, or resend it below.'
              : error.message
          );
        } else {
          setShowResendConfirmation(false);
          window.history.replaceState({}, '', '/dashboard');
          onClose();
          resetForm();
        }
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          setError(error.message);
        } else {
          setMessage('Check your email for a password reset link!');
        }
      } else if (mode === 'reset') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.updateUser({ password });
        if (error) {
          setError(error.message);
        } else {
          setMessage('Password updated successfully! You can now sign in.');
          clearPasswordReset();
          window.history.replaceState({}, '', '/dashboard');
          setTimeout(() => {
            setMode('login');
            setPassword('');
            setConfirmPassword('');
            setMessage(null);
          }, 2000);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setMessage(null);
  };

  const switchMode = (newMode: 'login' | 'forgot' | 'reset') => {
    setMode(newMode);
    setShowResendConfirmation(false);
    resetForm();
  };

  const handleResendConfirmation = async () => {
    if (!email.trim()) {
      setError('Enter your email address first.');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    const { error } = await resendConfirmation(email);
    if (error) {
      setError(error.message);
    } else {
      setMessage('Confirmation email sent. Check your inbox and spam folder.');
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (mode === 'reset') {
      // Clear the hash from URL when closing reset mode
      window.history.replaceState({}, '', window.location.pathname);
    }
    setMode('login');
    onClose();
  };

  // Show modal if open OR if in reset mode
  if (!isOpen && mode !== 'reset') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-none sm:rounded-2xl shadow-2xl w-full h-full sm:h-auto sm:max-w-md sm:mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5" style={{ backgroundColor: '#15452f' }}>
          <h2 className="text-2xl font-bold text-white">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'forgot' && 'Reset Password'}
            {mode === 'reset' && 'Set New Password'}
          </h2>
          <p className="text-emerald-100 mt-1">
            {mode === 'login' && 'Sign in to access your account'}
            {mode === 'forgot' && "We'll send you a reset link"}
            {mode === 'reset' && 'Enter your new password below'}
          </p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}
          
          {message && (
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg text-emerald-700 dark:text-emerald-300 text-sm">
              {message}
            </div>
          )}
          
          {mode !== 'reset' && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
          )}
          
          {(mode === 'login' || mode === 'reset') && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {mode === 'reset' ? 'New Password' : 'Password'}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
          )}
          
          {mode === 'reset' && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
          )}
          
          {showResendConfirmation && mode === 'login' && (
            <button
              type="button"
              onClick={handleResendConfirmation}
              disabled={loading}
              className="w-full py-2 px-4 border border-emerald-600 text-emerald-700 dark:text-emerald-300 font-medium rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors disabled:opacity-50"
            >
              Resend confirmation email
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : (
              <>
                {mode === 'login' && 'Sign In'}
                {mode === 'forgot' && 'Send Reset Link'}
                {mode === 'reset' && 'Update Password'}
              </>
            )}
          </button>
        </form>
        
        {/* Footer Links */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700 text-center text-sm">
          {mode === 'login' && (
            <>
              <button
                onClick={() => switchMode('forgot')}
                className="text-emerald-600 dark:text-emerald-400 hover:underline"
              >
                Forgot password?
              </button>
            </>
          )}
          {mode === 'login' && onCreateAccountClick && (
            <button
              onClick={onCreateAccountClick}
              className="ml-2 text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              Create account
            </button>
          )}
          {mode === 'forgot' && (
            <button
              onClick={() => switchMode('login')}
              className="text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              Back to sign in
            </button>
          )}
          {mode === 'reset' && (
            <button
              onClick={() => switchMode('login')}
              className="text-emerald-600 dark:text-emerald-400 hover:underline"
            >
              Back to sign in
            </button>
          )}
        </div>
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AuthModal;
